/* ============================================================
   Compliance Calendar

   Every instrument rendered as a dated obligation with a COMPUTED
   status. This is the module the audit's headline finding demanded:
   nothing here can describe an in-force obligation as "upcoming",
   because nothing here stores a status string at all.
   ============================================================ */

import {
  REGULATIONS,
  statusOf,
  STATUS_META,
  VERIFICATION_META,
  IMMINENT_WINDOW_DAYS,
  REGISTER_MAX_AGE_DAYS,
  registerFreshness
} from '../../data/regulations.js';
import { html, raw } from '../../shared/html.js';
import { icon } from '../../shared/icons.js';
import { shortDate, daysUntil, relativeDays } from '../../shared/format.js';
import { PrintButton } from '../../components/PrintButton.js';
import { readable } from '../../shared/tone.js';

const PROFILES = [
  { id: '', label: 'All operations' },
  { id: 'importer', label: 'Importer' },
  { id: 'clearing-agent', label: 'Clearing agent' },
  { id: 'transporter', label: 'Transporter' },
  { id: 'warehouse', label: 'Warehouse' }
];

const TONE_FOR_STATUS = {
  'in-force': 'positive',
  imminent: 'warning',
  announced: 'info',
  superseded: 'muted',
  revoked: 'danger'
};

const TONE_COLOR = {
  positive: 'var(--ca-positive)',
  warning: 'var(--ca-warning)',
  info: 'var(--ca-info)',
  muted: 'var(--ca-text-quaternary)',
  danger: 'var(--ca-danger)'
};

export function CompliancePage() {
  const page = document.createElement('div');
  page.className = 'container-narrow page animate-fade-in';

  page.innerHTML = html`
    <header class="page-head">
      <h1>Compliance Calendar</h1>
      <p>
        Every instrument as a dated obligation. Status is computed from today’s date against the
        effective date — never stored — so this screen cannot go out of step with the law.
      </p>
    </header>

    <div class="card" style="margin-bottom: var(--ca-space-5);">
      <label class="field" style="margin-bottom:0;">
        <span class="field-label">Show obligations for</span>
        <select id="profile" class="input-field">
          ${PROFILES.map((p) => html`<option value="${p.id}">${p.label}</option>`)}
        </select>
      </label>
    </div>

    <div id="compliance-results"></div>
  `;

  const results = page.querySelector('#compliance-results');
  const select = page.querySelector('#profile');

  const paint = () => renderList(results, select.value);
  select.addEventListener('change', paint);
  paint();

  return page;
}

function renderList(container, profile) {
  const now = new Date();

  const items = REGULATIONS.filter((r) => !profile || r.appliesTo.includes(profile))
    .map((r) => ({ ...r, status: statusOf(r, now), days: daysUntil(r.effectiveDate, now) }))
    .sort((a, b) => {
      /* Annulled sorts first. An imminent deadline is something to plan
         for; an annulled instrument is a correction to something the
         reader may already have paid for or fitted, and it stops being
         useful the moment it is buried. Every status must appear here —
         a missing key yields undefined, and undefined comparisons are
         all false, which scatters the unranked items silently. */
      const rank = { revoked: 0, imminent: 1, announced: 2, 'in-force': 3, superseded: 4 };
      if (rank[a.status] !== rank[b.status]) return rank[a.status] - rank[b.status];
      return new Date(b.effectiveDate) - new Date(a.effectiveDate);
    });

  container.replaceChildren();
  container.id = 'compliance-results';

  if (items.length === 0) {
    container.innerHTML = html`
      <div class="empty-state">
        <h3>No instruments for this profile</h3>
        <p>Try “All operations” to see the full register.</p>
      </div>
    `;
    return;
  }

  const counts = items.reduce((acc, i) => ({ ...acc, [i.status]: (acc[i.status] || 0) + 1 }), {});

  const summary = document.createElement('div');
  summary.className = 'card card-tinted';
  summary.style.marginBottom = 'var(--ca-space-5)';
  summary.innerHTML = html`
    <div class="row" style="flex-wrap: wrap; gap: var(--ca-space-2);">
      ${['imminent', 'announced', 'in-force']
        .filter((s) => counts[s])
        .map((s) => {
          const color = TONE_COLOR[TONE_FOR_STATUS[s]];
          return html`<span class="badge"
            style="background: color-mix(in srgb, ${color} 12%, transparent); color: ${readable(color)};"
          >
            <span class="num">${counts[s]}</span> ${STATUS_META[s].label.toLowerCase()}
          </span>`;
        })}
    </div>
    <p style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary); margin-top: var(--ca-space-3);">
      “Imminent” means it takes effect within ${IMMINENT_WINDOW_DAYS} days. Renewable instruments
      are marked — those are the ones that will bite you again next year.
    </p>
    ${(() => {
      /* Always shown, fresh or stale. A notice that only appears once
         something is wrong teaches readers that silence means verified,
         and this page is built to work offline — a visitor can be
         reading it weeks after they last connected, with the app
         behaving identically either way. Stating the age every time is
         what makes the number mean anything when it grows. */
      const f = registerFreshness();
      return html`<p
        style="font-size: var(--ca-text-xs); margin-top: var(--ca-space-2);
               color: ${f.stale ? 'var(--ca-warning-text)' : 'var(--ca-text-quaternary)'};"
      >
        ${f.stale ? raw(icon('alert', 12)) : ''} ${f.label}.
        ${f.stale
          ? `That is past the ${REGISTER_MAX_AGE_DAYS}-day mark this register holds itself to. ` +
            `Instruments here may have moved — check anything you are about to pay against ` +
            `the gazette before relying on it.`
          : `Status is computed from today’s date; whether the instruments themselves are ` +
            `current is a separate question, and this is the answer to it.`}
      </p>`;
    })()}
  `;
  container.appendChild(summary);

  for (const item of items) {
    container.appendChild(obligationCard(item));
  }

  container.appendChild(
    PrintButton({
      targetId: 'compliance-results',
      title: 'Compliance Calendar',
      subtitle: PROFILES.find((p) => p.id === profile)?.label || 'All operations'
    })
  );
}

/* What kind of thing the citation points at. Silent for a statutory
   instrument — that is the case the reader already assumes, and saying
   "this is a law" under every law is noise that trains people to skip
   the line where it matters. It speaks up for the three records that
   are the authority's own notice rather than an instrument, because
   that difference changes what a reader can do if they disagree with
   it. */
function verificationNote(item) {
  const meta = VERIFICATION_META[item.verification];
  if (!meta || !meta.note) return '';
  return html`<br /><span style="color: var(--ca-text-tertiary);"
    ><strong style="font-weight: var(--ca-weight-medium);">${meta.label}.</strong> ${meta.note}</span
  >`;
}

function obligationCard(item) {
  const meta = STATUS_META[item.status];
  const color = TONE_COLOR[TONE_FOR_STATUS[item.status]];

  const card = document.createElement('article');
  card.className = 'card avoid-break';
  card.style.marginBottom = 'var(--ca-space-3)';

  card.innerHTML = html`
    <div class="row" style="align-items: flex-start; gap: var(--ca-space-3); margin-bottom: var(--ca-space-3);">
      <div style="flex: 1; min-width: 0;">
        <!-- h2: each obligation is a top-level item under the page h1,
             and there is no h2 above it to be a child of. -->
        <h2 style="font-size: var(--ca-text-base); margin-bottom: var(--ca-space-1);">${item.name}</h2>
        <div style="font-size: var(--ca-text-xs); color: var(--ca-text-quaternary);">
          ${item.authority} · ${item.governingAct}
        </div>
      </div>
      <span class="badge" style="background: color-mix(in srgb, ${color} 12%, transparent); color: ${readable(color)};">
        ${meta.label}
      </span>
    </div>

    ${item.revoked
      ? /* Above the summary, not below it. The reader may have already
           acted on this instrument — paid a fee, fitted equipment,
           appointed someone — and the correction is worth nothing if it
           sits under the obligation it corrects. It also names what
           survives, because "stop" without "instead" leaves an operator
           worse off than the wrong obligation did. */
        html`
          <div class="notice notice-danger" style="margin-bottom: var(--ca-space-3);">
            <span style="flex-shrink:0;margin-top:2px;">${raw(icon('alert', 15))}</span>
            <div style="font-size: var(--ca-text-xs);">
              <strong style="font-weight: var(--ca-weight-medium);"
                >Annulled ${item.revoked.when} — ${item.revoked.by}.</strong
              >
              ${item.revoked.note}
              <div style="margin-top: var(--ca-space-2);">${item.revoked.fallBack}</div>
            </div>
          </div>
        `
      : ''}

    <div class="row" style="gap: var(--ca-space-4); flex-wrap: wrap; margin-bottom: var(--ca-space-3);
                            font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
      <span class="row" style="gap: var(--ca-space-1);">
        ${raw(icon('calendar', 13))} ${shortDate(item.effectiveDate)}
        <span style="color: var(--ca-text-quaternary);">(${relativeDays(item.days)})</span>
      </span>
      ${item.renewal
        ? html`<span class="row" style="gap: var(--ca-space-1);">
            ${raw(icon('refresh', 13))} ${item.renewal}
          </span>`
        : ''}
      ${item.cost
        ? html`<span class="row" style="gap: var(--ca-space-1);">
            ${raw(icon('coins', 13))} ${item.cost}
          </span>`
        : ''}
    </div>

    <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary); margin-bottom: var(--ca-space-3);">
      ${item.summary}
    </p>

    <div style="padding: var(--ca-space-3); background: var(--ca-surface-muted);
                border-radius: var(--ca-radius-md); font-size: var(--ca-text-xs);
                color: var(--ca-text-secondary); line-height: var(--ca-leading-snug);">
      <strong style="font-weight: var(--ca-weight-medium);">What to do</strong><br />
      ${item.action}
      ${item.portal
        ? html`<br /><a
            href="${item.portal}"
            target="_blank"
            rel="noopener noreferrer"
            style="font-weight: var(--ca-weight-medium);"
            >${item.portal.replace('https://', '')} &rarr;</a
          >`
        : ''}
    </div>

    <div style="margin-top: var(--ca-space-3); font-size: var(--ca-text-2xs); color: var(--ca-text-quaternary);">
      Source: ${item.source}${item.sourceRef ? ` · ${item.sourceRef}` : ''}
      <br />
      Governing Act: ${item.governingAct}
      ${!item.sourceRef
        ? /* No locator at all. Kept as a branch rather than deleted:
             every instrument carries one today, and the check refuses a
             new one that does not, but a renderer that assumes its data
             is clean is how the last overclaim survived twelve records
             and an external review. */
          html`<br /><span style="color: var(--ca-warning-text);"
            >Notice number not recorded — verify against the Act before relying on the date.</span
          >`
        : verificationNote(item)}
    </div>
  `;

  return card;
}
